import { useState } from 'react';
import { CopyField, FormGrid, FormPanel, LabeledField, SelectField, TextField } from '@sparkery/ui';
import {
  CHANNELS,
  DESTINATIONS,
  PLACEMENTS,
  buildReferralUrl,
  type Channel,
  type CreateLinkInput,
} from '@sparkery/contracts';
import { ApiError } from '../../lib/api.ts';
import { useCreateLink, useSettings } from '../../lib/partner-admin.ts';

/**
 * Generating a referral link.
 *
 * Partners never author these. The spreadsheet's own warning is that partners writing
 * their own tags produce "Instagram", "instagram", "IG" and "insta" as four different
 * sources in the report — so the tags come from one function, and the preview below is
 * that same function, run in the browser against the same settings the server will use.
 *
 * The preview matters because a link is often pasted into print. Once generated, the
 * URL is stored exactly as shown and a later settings change never rewrites it.
 */
export function GenerateLinkForm({
  partnerId,
  partnerSlug: slug,
  archived,
  onCancel,
}: {
  partnerId: string;
  /** The stored tag, not the name — a renamed partner keeps their original source. */
  partnerSlug: string;
  archived: boolean;
  onCancel: () => void;
}) {
  const [label, setLabel] = useState('');
  const [destination, setDestination] = useState<string>(DESTINATIONS[0].url);
  const [channel, setChannel] = useState<Channel | ''>('');
  const [placement, setPlacement] = useState('');
  const create = useCreateLink(partnerId);
  const settings = useSettings();

  const input: CreateLinkInput = {
    label: label.trim() || 'Untitled',
    destination,
    ...(channel ? { channel } : {}),
    ...(placement ? { placement: placement as (typeof PLACEMENTS)[number] } : {}),
  };

  // The same builder the server runs, so what an admin sees is what gets stored. If
  // the settings have not loaded there is nothing honest to show yet.
  const preview = settings.data
    ? buildReferralUrl({ ...input, partnerName: slug }, settings.data)
    : '';

  function submit() {
    if (!label.trim()) return;
    create.mutate({ ...input, label: label.trim() }, { onSuccess: onCancel });
  }

  if (archived) return null;

  return (
    <FormPanel
      title="Add a referral link"
      confirmLabel={create.isPending ? 'Creating…' : 'Create link'}
      confirmDisabled={!label.trim() || create.isPending}
      onCancel={onCancel}
      onConfirm={submit}
      note="Blank Channel or Placement falls back to the programme defaults set in Settings."
    >
      <FormGrid>
        <TextField
          id="link-label"
          label="Label"
          value={label}
          onChange={(event) => setLabel(event.target.value)}
          placeholder="Autumn newsletter"
          autoComplete="off"
        />

        <SelectField
          id="link-channel"
          label="Channel"
          value={channel}
          onChange={(event) => setChannel(event.target.value as Channel | '')}
          options={[
            // The blank option is the programme default, named so it is a choice
            // rather than an empty box.
            { value: '', label: `Default (${settings.data?.defaultChannel ?? 'partner'})` },
            ...CHANNELS.map((option) => ({ value: option, label: option })),
          ]}
        />

        <SelectField
          id="link-placement"
          label="Placement"
          value={placement}
          onChange={(event) => setPlacement(event.target.value)}
          options={[
            { value: '', label: 'None' },
            ...PLACEMENTS.map((option) => ({ value: option, label: option })),
          ]}
        />

        <SelectField
          id="link-destination"
          label="Destination"
          value={destination}
          onChange={(event) => setDestination(event.target.value)}
          options={DESTINATIONS.map((option) => ({ value: option.url, label: option.label }))}
        />
      </FormGrid>

      {preview ? (
        <LabeledField label="Generated link">
          <CopyField value={preview} label="Generated link" />
        </LabeledField>
      ) : null}

      {create.error ? (
        <p className="partnersFormError" role="alert">
          {create.error instanceof ApiError
            ? create.error.message
            : 'Could not generate that link.'}
        </p>
      ) : null}

      {create.data ? (
        <p className="partnersFormOk" role="status">
          <strong>{create.data.label}</strong> generated and added below.
        </p>
      ) : null}
    </FormPanel>
  );
}
